import React from 'react';
import { CtaButton, type CtaButtonColorVariant } from '../CtaButton/CtaButton';
import { Image } from '../Image/Image';
import { TestimonialContent } from '../TestimonialContent/TestimonialContent';
import { Video, type VideoPlayBehavior } from '../Video/Video';
import { hasCtaDestination } from '../../utils/ctaAction';
import type { CtaAction } from '../../utils/ctaAction';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import './Testimonial.scss';

/**
 * Testimonial - Customer testimonial card with quote and attribution.
 *
 * Desktop (≥768): side-by-side layout (content + media)
 * Tablet (≤767): image stacks below content (275px min-height)
 * Mobile (≤479): image 200px min-height, quotee stacks vertically
 *
 * Centered variant (textAlign='center'): content is center-aligned and the
 * media slot is omitted. Left/right layouts render the media slot only when
 * `mediaSrc` is set; without it the content spans the full width (text-only
 * testimonial).
 */

/**
 * @polySemanticUsage About this block: displays social proof — customer quotes, success stories,
 * or endorsements. Typically includes the quote text, attribution (name, title, company or
 * brand), and an optional headshot or logo. Use it to build trust and credibility.
 * Detection priority: when a STANDALONE section (a single visual element, not part of
 * another block) shows a seller quote, attribution, brand name, or brand logo, always map it to
 * Testimonial — no other block. This rule does not apply to testimonials embedded inside another
 * block (e.g. a SuperCard with a quote): leave those in their host block.
 * Source headers and surrounding prose: if the source testimonial has a short header built
 * into the block, place it in heading — it renders below the eyebrow or brand logo and above the
 * quote as a card heading. Do not place headers in eyebrow: that slot is a short brand-scale
 * label and is mutually exclusive with the brand logo. If the source has prose outside the quote
 * that does not fit a card heading, map that prose to a RichText block placed above the
 * Testimonial instead of forcing it into heading or the quote. Never wrap that surrounding prose
 * into another block (e.g. a SuperCard) and never split the testimonial apart to make room for
 * it — the pattern is always RichText above, Testimonial below, as sibling blocks.
 * Fill quote with the raw testimonial text and no surrounding quotation marks of any kind
 * (" “ ” « » „) — the component adds the marks itself in the style of the page's language, so
 * quotes belong to the presentation, not the content.
 * The media slot supports video: set videoUrl alongside mediaSrc (used as the poster) to render a
 * click-to-play video instead of a still image. The media slot is omitted entirely when textAlign
 * is center, so do not pair videoUrl with the centered variant.
 * The eyebrow slot above the quote takes either a brand logo (eyebrowLogoSrc) or short eyebrow
 * text (eyebrow), never both — populate exactly one. When both are set the logo renders and the
 * text is dropped.
 */
export interface TestimonialProps {
  /**
   * Testimonial quote text, without surrounding quotation marks.
   * @polyHelp Enter the quote without quotation marks. They are added automatically in the style of the page's language.
   */
  quote: string;

  /** Person's name */
  name: string;

  /** Person's title/role */
  title?: string;

  /**
   * Optional eyebrow text above the quote. Mutually exclusive with
   * `eyebrowLogoSrc` — the logo wins if both are provided.
   * @polyLabel Eyebrow Text
   * @polyHelp Short label above the quote. Use this OR the Eyebrow Logo, never both — the logo takes precedence.
   */
  eyebrow?: string;

  /**
   * Optional eyebrow logo image URL. Mutually exclusive with `eyebrow` — this
   * logo wins if both are provided.
   * @polyType Url
   * @polyLabel Eyebrow Logo URL
   * @polyHelp Brand logo above the quote. Use this OR the Eyebrow Text, never both — the logo takes precedence.
   */
  eyebrowLogoSrc?: string;

  /**
   * Accessible name for the eyebrow logo. Required whenever `eyebrowLogoSrc` is
   * set: the logo identifies the brand behind the quote, so it carries meaning
   * rather than decoration. Omitting it falls back to a label built from `name`,
   * which ties the logo to the testimonial but cannot name the brand, and fires a
   * dev warning — an empty alt would instead mark a meaningful image decorative
   * and hide it entirely (WCAG 1.1.1).
   * @polyLabel Eyebrow Logo Alt Text
   * @polyHelp Required when Eyebrow Logo URL is set — name the brand shown in the logo.
   */
  eyebrowLogoAlt?: string;

  /**
   * Optional card heading rendered below the eyebrow/logo row and above the
   * quote. Use it when the testimonial carries its own short header (e.g. a
   * campaign line like "Earth Rated goes global with a click").
   * @polyLabel Heading
   * @polyHelp Short header above the quote and below the eyebrow or brand logo. Leave empty when the quote stands alone.
   */
  heading?: string;

  /**
   * Optional headshot image URL
   * @polyType Url
   * @polyLabel Headshot URL
   */
  headshotSrc?: string;

  /** Headshot alt text */
  headshotAlt?: string;

  /**
   * Optional main media image URL. When `videoUrl` is also set, this URL is
   * used as the video poster; otherwise it renders as a still image.
   * @polyType Url
   * @polyLabel Media URL
   */
  mediaSrc?: string;

  /** Media alt text (also used as the video poster alt when `videoUrl` is set) */
  mediaAlt?: string;

  /**
   * Optional video URL. When set (alongside `mediaSrc` as the poster), the
   * media slot renders a Video with a play overlay instead of a still image.
   * @polyType Url
   * @polyLabel Video URL
   */
  videoUrl?: string;

  /**
   * Video play behavior — mirrors the Video component contract.
   * @polyType SelectList
   * @polyLabel Video Play Behavior
   * @polyOptions Lightbox=lightbox,Inline=inline,Navigate=navigate
   */
  videoPlayBehavior?: VideoPlayBehavior;

  /**
   * Media position relative to content
   * @polyOptions Left=left,Right=right
   */
  mediaPosition?: 'left' | 'right';

  /**
   * Text alignment — 'left' (default) or 'center' (no-image full-width variant)
   * @polyLabel Text Alignment
   * @polyOptions Left=left,Center=center
   */
  textAlign?: 'left' | 'center';

  /** Link text displayed below attribution */
  linkText?: string;

  /**
   * Link URL
   * @polyType Url
   */
  linkHref?: string;

  /**
   * What the link does
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Link by default. Watch video plays the URL in a player on this page. Download downloads the file when the browser permits it. Popup opens the Popup block matching the Target Popup ID.
   * @polyDefault link
   * @polyLabel Link Action
   * @polyTranslatable false
   */
  linkAction?: CtaAction;

  /**
   * Popup ID to open when Link Action is Popup.
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  linkTargetPopupId?: string;

  /** Primary CTA text (medium button) */
  ctaText?: string;

  /**
   * Primary CTA URL
   * @polyType Url
   */
  ctaHref?: string;

  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  ctaAction?: CtaAction;

  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  ctaTargetPopupId?: string;

  /**
   * Primary CTA color variant
   * @polyType SelectList
   * @polyOptions Smile Orange=primary,Contrast=contrast,Outlined=secondary,Text Link=tertiary,Off-White=offWhite
   * @polyLabel Primary CTA Color
   */
  ctaColorVariant?: CtaButtonColorVariant;

  /** Secondary CTA text (optional dual CTA) */
  secondaryCtaText?: string;

  /**
   * Secondary CTA URL
   * @polyType Url
   */
  secondaryCtaHref?: string;

  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  secondaryCtaAction?: CtaAction;

  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  secondaryCtaTargetPopupId?: string;

  /**
   * Secondary CTA color variant
   * @polyType SelectList
   * @polyOptions Smile Orange=primary,Contrast=contrast,Outlined=secondary,Text Link=tertiary,Off-White=offWhite
   * @polyLabel Secondary CTA Color
   */
  secondaryCtaColorVariant?: CtaButtonColorVariant;

  /**
   * Background variant
   * @polyOptions White=white,Off-White=off-white
   */
  variant?: 'white' | 'off-white';

  /** Called when the testimonial video starts playing, from the media slot or a Watch video link. Programmatic only — not a CMS field. */
  onVideoPlay?: () => void;

  /** Called when the testimonial video lightbox or the Watch video link's modal closes. Programmatic only — not a CMS field. */
  onVideoClose?: () => void;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

export const Testimonial: React.FC<TestimonialProps> = ({
  quote,
  name,
  title,
  eyebrow,
  eyebrowLogoSrc,
  eyebrowLogoAlt,
  heading,
  headshotSrc,
  headshotAlt,
  mediaSrc,
  mediaAlt,
  videoUrl,
  videoPlayBehavior = 'lightbox',
  mediaPosition = 'right',
  textAlign = 'left',
  linkText,
  linkHref,
  linkAction = 'link',
  linkTargetPopupId,
  ctaText,
  ctaHref,
  ctaAction,
  ctaTargetPopupId,
  ctaColorVariant = 'primary',
  secondaryCtaText,
  secondaryCtaHref,
  secondaryCtaAction,
  secondaryCtaTargetPopupId,
  secondaryCtaColorVariant = 'secondary',
  variant = 'white',
  onVideoPlay,
  onVideoClose,
  anchorId,
}) => {
  if (process.env.NODE_ENV !== 'production') {
    if (eyebrow && eyebrowLogoSrc) {
      // eslint-disable-next-line no-console -- authoring diagnostic for the either/or misconfig
      console.warn(
        '[Testimonial] Both `eyebrow` and `eyebrowLogoSrc` were provided; rendering the logo and ignoring `eyebrow`. Use one or the other.',
      );
    }
    if (eyebrowLogoSrc && !eyebrowLogoAlt) {
      // eslint-disable-next-line no-console -- authoring accessibility diagnostic
      console.warn(
        '[Testimonial] `eyebrowLogoSrc` is set without `eyebrowLogoAlt`; the logo falls back to a label built from `name`, which does not identify the brand. Provide `eyebrowLogoAlt` with the brand name (WCAG 1.1.1).',
      );
    }
  }

  const classes = [
    'testimonial',
    variant === 'off-white' && 'testimonial--off-white',
    textAlign === 'center' && 'testimonial--centered',
    mediaSrc && mediaPosition === 'left' && 'testimonial--media-left',
  ].filter(Boolean).join(' ');

  // An off-Amazon destination opens in a new tab with a diagonal arrow.
  // Video and download links stay on the page, so only plain links get the off-site arrow and new tab.
  const isExternalLink = linkAction === 'link' && !!linkHref && linkHref.startsWith('http') && !linkHref.includes('amazon.com');

  return (
    <article className={classes} {...blockAnchorAttributes(anchorId)}>
      <div className="testimonial__layout">
        <TestimonialContent
          quote={quote}
          name={name ?? ''}
          title={title}
          eyebrow={eyebrow}
          logoSrc={eyebrowLogoSrc}
          logoAlt={eyebrowLogoAlt}
          heading={heading}
          headshotSrc={headshotSrc}
          headshotAlt={headshotAlt}
          linkText={linkText}
          linkHref={linkHref}
          linkAction={linkAction}
          linkTargetPopupId={linkTargetPopupId}
          linkDirection={isExternalLink ? 'diagonal' : 'right'}
          linkOpensNewTab={isExternalLink}
          onVideoOpen={onVideoPlay}
          onVideoClose={onVideoClose}
        >
          {ctaText && hasCtaDestination(ctaHref, ctaAction, ctaTargetPopupId) && (
            <div className="testimonial__cta-group">
              <CtaButton
                href={ctaHref}
                action={ctaAction}
                popupTargetId={ctaTargetPopupId}
                colorVariant={ctaColorVariant}
                size="medium"
              >
                {ctaText}
              </CtaButton>
              {secondaryCtaText && hasCtaDestination(secondaryCtaHref, secondaryCtaAction, secondaryCtaTargetPopupId) && (
                <CtaButton
                  href={secondaryCtaHref}
                  action={secondaryCtaAction}
                  popupTargetId={secondaryCtaTargetPopupId}
                  colorVariant={secondaryCtaColorVariant}
                  size="medium"
                >
                  {secondaryCtaText}
                </CtaButton>
              )}
            </div>
          )}
        </TestimonialContent>

        {textAlign !== 'center' && mediaSrc && (
          <div className="testimonial__media">
            {videoUrl ? (
              <Video
                posterUrl={mediaSrc}
                posterAlt={mediaAlt ?? `Video thumbnail for ${name}`}
                videoUrl={videoUrl}
                playBehavior={videoPlayBehavior}
                onPlay={onVideoPlay}
                onClose={onVideoClose}
              />
            ) : (
              <Image bare url={mediaSrc} alt={mediaAlt ?? `Photo of ${name}`} />
            )}
          </div>
        )}
      </div>
    </article>
  );
};
